import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Users, Check, Clock, X, ArrowUp, ArrowDown, Minus } from 'lucide-react';
import { useCourtParticipantsAttendance, AttendanceStatus } from '@/hooks/useCourtParticipantsAttendance';
import { Skeleton } from '@/components/ui/skeleton';

interface CourtParticipantsListProps {
  eventId: string;
  encounterNumber: number;
}

const getAttendanceBadge = (status: AttendanceStatus) => {
  switch (status) {
    case 'confirmed':
      return (
        <Badge className="bg-green-100 text-green-800 hover:bg-green-100 flex items-center gap-1">
          <Check className="w-3 h-3" />
          Confirmado
        </Badge>
      );
    case 'not_attending':
      return (
        <Badge className="bg-red-100 text-red-800 hover:bg-red-100 flex items-center gap-1">
          <X className="w-3 h-3" />
          No asiste
        </Badge>
      );
    case 'pending':
    default:
      return (
        <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 flex items-center gap-1">
          <Clock className="w-3 h-3" />
          Pendiente
        </Badge>
      );
  }
};

const getMovementBadge = (label: string, type: string) => {
  switch (type) {
    case 'up':
      return (
        <Badge className="bg-green-100 text-green-800 hover:bg-green-100 flex items-center gap-1">
          <ArrowUp className="w-3 h-3" />
          {label}
        </Badge>
      );
    case 'down':
      return (
        <Badge className="bg-red-100 text-red-800 hover:bg-red-100 flex items-center gap-1">
          <ArrowDown className="w-3 h-3" />
          {label}
        </Badge>
      );
    case 'neutral':
    default:
      return (
        <Badge className="bg-muted text-muted-foreground hover:bg-muted flex items-center gap-1">
          <Minus className="w-3 h-3" />
          {label}
        </Badge>
      );
  }
};

const getInitials = (name: string): string => {
  return name
    .split(' ')
    .map(word => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

const CourtParticipantsList: React.FC<CourtParticipantsListProps> = ({
  eventId,
  encounterNumber
}) => {
  const { participants, courtNumber, isLoading } = useCourtParticipantsAttendance(
    eventId,
    encounterNumber
  );

  if (isLoading) {
    return (
      <Card className="mt-4">
        <CardHeader className="pb-3">
          <Skeleton className="h-6 w-48" />
        </CardHeader>
        <CardContent className="space-y-3">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Skeleton className="h-8 w-8 rounded-full" />
                <Skeleton className="h-4 w-24" />
              </div>
              <Skeleton className="h-6 w-20" />
            </div>
          ))}
        </CardContent>
      </Card>
    );
  }

  if (participants.length === 0) {
    return null;
  }

  return (
    <Card className="mt-4">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Users className="w-5 h-5 text-primary" />
          Participantes - Cancha {courtNumber || 1}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {participants.map((participant, index) => (
          <div 
            key={`${participant.playerName}-${index}`}
            className="flex items-center justify-between py-2 border-b border-border last:border-0"
          >
            <div className="flex items-center gap-3">
              <span className="font-medium text-foreground">
                {participant.playerName}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {participant.movementLabel && participant.movementType && (
                getMovementBadge(participant.movementLabel, participant.movementType)
              )}
              {getAttendanceBadge(participant.attendanceStatus)}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

export default CourtParticipantsList;
